import { LinkButton } from "@/ui/next/LinkButton";
import { useTranslation } from "next-i18next";

import { CONTACT_US_ROUTE } from "@/common/static/routes.static";
import { cn } from "@/common/utils/css.utils";
import {
  HELP_SUPPORT_EMAIL,
  HelpContactIcon,
  HelpMailIcon,
} from "@/features/help/help.static";
import { AnchorButton } from "@/ui/cva/Button";
import { Heading } from "@/ui/cva/Heading";
import { Body } from "@/ui/typography/Body";

type HelpContactStripProps = {
  className?: string;
};

/**
 * "Still need help?" panel. Info-panel colours from the design system
 * (Flavour-100 surface, Brand-600 text), text left, actions right on md+,
 * stacked full-width on mobile with the primary action on top.
 */
export const HelpContactStrip = ({ className }: HelpContactStripProps) => {
  const { t } = useTranslation("common");

  return (
    <section
      className={cn(
        "mt-12 flex w-full flex-col gap-6 rounded-[4px] bg-flavour-100 p-4 text-brand-600 md:flex-row md:items-center md:justify-between md:p-6",
        className
      )}
      aria-labelledby="help-contact-strip-title"
    >
      <div className="flex items-start gap-3">
        <HelpContactIcon className="mt-0.5 h-6 w-6 shrink-0" aria-hidden />
        <div>
          <Heading
            as="h2"
            size="h5"
            id="help-contact-strip-title"
            className="text-brand-600"
          >
            {t("help-contact-cta-title", "Still need help?")}
          </Heading>
          <Body as="p" className="mt-1 text-brand-600">
            {t("help-contact-hours", "Weekdays 09:30 to 17:00")}
          </Body>
        </div>
      </div>
      <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-center">
        <LinkButton
          href={CONTACT_US_ROUTE}
          appearance="solid"
          color="brand"
          size="small"
          className="w-full md:order-2 md:w-auto"
        >
          {t("help-contact-cta-link", "Contact us")}
        </LinkButton>
        <AnchorButton
          href={`mailto:${HELP_SUPPORT_EMAIL}`}
          appearance="outlined"
          color="brand"
          size="small"
          className="w-full md:order-1 md:w-auto"
          leftIcon={<HelpMailIcon className="h-5 w-5" />}
        >
          {t("help-contact-email", "Email")}
        </AnchorButton>
      </div>
    </section>
  );
};
